import React from "react";
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate } from "@fortawesome/pro-light-svg-icons";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import ApiKeyContext from "../../../../contexts/ApiKeyContext";
import CatalogContext from "../../../../contexts/CatalogContext";
import PageContext from "../../../../contexts/PageContext";
import { useProductDetailContext } from "../../../../hooks/useProductDetailContext";

import { useAuth } from "../../../../auth/useAuth";

import { download, getBossWrapper, processImagePath, trackEvent, } from "../../../../util/Functions";

import ProductMedia from "./ProductMedia";
import ItemInquiryButton from "./item_inquiry/ItemInquiryButton";
import ProductAttributes from "./ProductAttributes";
import ProductAttribute from "./ProductAttribute";
import BookmarkItemButton from "../../components/bookmark/BookmarkItemButton";
import LoadMask from "../../../../components/LoadMask";

import ProductDetailMenu from "./ProductDetailMenu";
import ProposalItemActions from "../proposal/ProposalItemActions";

import "./ProductDetail.scss";

/** @jsxImportSource @emotion/react */
import { CanceledError } from "axios";
import { useCustomer } from "../../../../auth/useCustomer";
import { ProductUtil } from "../../../../util/Product.util";
import ScannableCodeFullscreen from "../scannable_code/ScannableCodeFullscreen";
import LiveApiClient from "../../../../api/LiveApiClient";
import { ArrayUtil } from "../../../../util/Array.util";
import { ObjectUtil } from "../../../../util/Object.util";
import { useLog } from "../../../../auth/useLog";
import { useSidebarContext } from "../../../../hooks/useSidebarContext";
import { ActionBus } from "../../../../models/ActionBus";
import ProductInvariationDialog from "../../../../components/invariation/ProductInvariationDialog";

const ItemRelationships = React.lazy(() => import('./ItemRelationships'));
const RequestProductButton = React.lazy(() => import("./request_product/request_product_button/RequestProductButton"));

export const NO_PRODUCT_FOUND_AUTO_CLOSE_TIMEOUT_MILLIS = 2_000;

export default function ProductDetail() {
	const { modalProps, setModalProps, actionBus } = useProductDetailContext();
	const [attributes, setAttributes] = React.useState(false);
	const [product, setProduct] = React.useState(null);
	const { currentUser, organizationMember } = useAuth();
	const preloaded_products = React.useRef(new Map());
	const preloaded_timeouts = React.useRef(new Set());
	const no_product_modal = React.useRef(null);
	const abort_controller = React.useRef(new AbortController());
	const [showQr, setShowQr] = React.useState(false);
	const [relatedItemCount, setRelatedItemCount] = React.useState(false);
	const SidebarContext = useSidebarContext();
	const Dialog = withReactContent(Swal);

	const [catalog] = React.useContext(CatalogContext);
	const { currentPage } = React.useContext(PageContext);

	// const { addNewChatMessage, setChatTarget } = useChatContext();
	const [loading, _setLoading] = React.useState(false);
	const loaded_item = React.useRef();
	const common_request_headers = React.useRef();

	const CustomerContext = useCustomer();
	const customerAllowed = CustomerContext.isAllowed(catalog);

	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	const LogContext = useLog();

	const empty_loading_container = (<div>
		<LoadMask show={loading} />
	</div>)

	const setLoading = React.useCallback(val => {
		getBossWrapper().classList.toggle("is-loading", val);
		_setLoading(val);
	}, [_setLoading]);

	// log change when component parts change
	React.useEffect(() => {
		if (!modalProps.item) return;
		if (!currentPage || Object.keys(currentPage).length === 0) return;

		LogContext.logPageView({
			page: currentPage,
			action: "view",
			item: modalProps.item
		});

	}, [
		modalProps.item,
		currentPage
	]);

	React.useEffect(() => {
		common_request_headers.current = {
			headers: {
				'api-key': API_KEY_CONTEXT.apiKey,
				'cache-control': !localStorage.getItem('nocache') && 'private',
				account: API_KEY_CONTEXT.account,
			},
			signal: abort_controller.current.signal
		};
	}, [API_KEY_CONTEXT?.apiKey, abort_controller.current]);

	const getProductRequest = React.useCallback(async (identifier, { isItemId } = {}) => {
		if (!LiveApiClient || !identifier) return Promise.resolve(null);
		const cacheKeyPrefixes = ["id", "sku", "upc"];
		// let's cap the cache since it's local and prevent some memory stuff; FIFO
		const maxCacheItems = 50 * cacheKeyPrefixes.length; // TODO ... maybe play with this on actual devices
		if (preloaded_products.current?.size > maxCacheItems) {
			preloaded_products.current.forEach((val, key, src) => {
				src.size > maxCacheItems && src.delete(key);
			});
		}

		const cachedItem = cacheKeyPrefixes.reduce((acc, key) => {
			if (acc) return acc;
			const cacheKey = `${key}::${identifier}`;
			if (preloaded_products.current.has(cacheKey)) {
				return preloaded_products.current.get(cacheKey);
			}
		}, null);
		if (cachedItem) return cachedItem;
		
		let item;
		if (isItemId) {
			item = await LiveApiClient.get(`/Item/${identifier}`, common_request_headers.current)
				.then(r => r.data);
		}
		else {
			const options = {
				...common_request_headers.current,
				params: {
					filter: JSON.stringify([
						[
							!isNaN(identifier) ? { property: "id", value: identifier } : undefined,
							{ property: "common_sku", value: identifier },
							{ property: "upc", value: identifier },
							{ property: "manufacturers_product_number", value: identifier },
							{ property: "rfid", value: identifier },
							{ property: "{inventory.stock_number}", value: identifier },
							{ property: "{inventory.serial_number}", value: identifier },
						].filter(v => v)
					]),
					limit: 1,
				},
			};
			
			item = await LiveApiClient.get(`/Item`, options).then(r => r.data?.at(0));
		}
		
		if (item) {
			preloaded_products.current
				.set(`id::${item.id}`, item)
				.set(`sku::${item.common_sku}`, item);
			item.upc?.trim().length && preloaded_products.current.set(`upc::${item.upc}`, item);
		}

		return item;
	}, [common_request_headers.current]);

	const cancelPreloads = () => {
		abort_controller.current.abort();
		abort_controller.current = new AbortController();
		preloaded_timeouts.current.forEach(clearTimeout);
		preloaded_timeouts.current.clear();
	};

	const getAttributes = async () => {
		if (attributes) return attributes;
		if (!modalProps.item) return;

		const attribute_endpoint = modalProps.item_attr_endpoint
			|| '/Catalog_Page/' + currentPage.id + '/attributes/details';
		const response = await LiveApiClient.get(attribute_endpoint, {
			headers: {
				account: API_KEY_CONTEXT.account,
				'api-key': API_KEY_CONTEXT.apiKey,
				'content-control': 'private'
			},
			signal: abort_controller.current.signal
		});
		setAttributes(response.data)
		return response.data;
	}

	const setItemInUrl = React.useCallback((id = undefined) => {
		const url = new URL(window.location.href);
		id
			? url.searchParams.set('item', id)
			: url.searchParams.delete('item');
		window.history.pushState(null, null, url.toString());
	}, []);

	const getProduct = React.useCallback(async (identifier, throwOnError = undefined, isItemId = undefined) => {
		if (!identifier) return;

		try {
			const product = await getProductRequest(identifier, { isItemId });
			if (!product) {
				throw new Error(`Product ${identifier} not found`);
			}
			return product;
		} catch (e) {
			if (throwOnError) {
				throw e;
			} else if (!(e instanceof CanceledError)) {
				console.error("Failed to load product", e);
			}
		}
	}, [getProductRequest]);

	React.useEffect(() => {
		setLoading(true);

		getAttributes()
			.then(async () => {
				setModalProps(current => ({ ...current, product_not_found: undefined }));
				const thisProduct = await getProduct(modalProps.item);

				setProduct(thisProduct);
				if (thisProduct?.variations) {
					preloadVariations(thisProduct.variations);
				}
				loaded_item.current = thisProduct ? modalProps.item : undefined;

				if (modalProps.item && !thisProduct) {
					setModalProps(current => ({ ...current, product_not_found: true }));
					setItemInUrl(null);
					no_product_modal?.current?.showModal();
					setTimeout(() => no_product_modal?.current?.close(), NO_PRODUCT_FOUND_AUTO_CLOSE_TIMEOUT_MILLIS);
				} else if (catalog?.options?.type !== 'public') {
					setItemInUrl(thisProduct?.id);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [
		modalProps.item,
		API_KEY_CONTEXT,
		catalog?.options?.type,
		catalog.detail_fieldset?.handle,
	]);

	const preload_image = React.useCallback(src => {
		new Image().src = processImagePath(src, "zoom");
	}, []);

	// Preload item variations
	const preloadVariations = React.useCallback(async variations => {
		if (!variations || !Object.keys(variations).length) {
			return;
		}
		const item_ids = [
			...Object.values(variations)
				.flat()
				.filter(([desc, id, distance]) => +distance <= 1 && id !== modalProps.item)
				.map(([desc, id]) => +id)
		].slice(0, 200);// Max out of preloading

		// load variations in paralleled batches; cancel current/remaining batches on request cancel (like closing detail)
		const collated_ids = ArrayUtil.Collate(item_ids, 5);
		for (const ids of collated_ids) {
			const variationProductsPreloads = await Promise.allSettled(ids.map(id => getProduct(id, true, true).then(product => {
				const images = product?.media?.filter(m => m.type === 'image');
				images?.length && images[0]?.source && preload_image(images[0].source);
			})));
			const unfulfilledByCancel = variationProductsPreloads.find(it => it.status !== "fulfilled" && it.reason instanceof CanceledError);
			if (unfulfilledByCancel) {
				break;
			}
		}
	}, [getProductRequest]);

	const media = product?.media?.reduce((r, a) => {
		r[a.type] ??= [];
		r[a.type].push(a);
		return r;
	}, Object.create(null));

	const downloadMedia = () => {
		Object.values(media).forEach(mediaByType =>
			mediaByType.forEach((m, index) =>
				preloaded_timeouts.current.add(setTimeout(() => download(processImagePath(m.source)), index * 500))
			)
		);
	};

	const changeItem = id => {
		cancelPreloads();
		setModalProps({ ...modalProps, item: id });
	}

	const showModal = React.useCallback(() => {
		actionBus?.send("load");
	}, []);

	const closeModal = React.useCallback(() => {
		cancelPreloads();
		setModalProps({ ...modalProps, item: null });
	}, [modalProps, setModalProps]);
	window.addEventListener("popstate", () => modalProps.item && setModalProps(null));


	const close_no_product_dialog = React.useCallback(() => {
		no_product_modal.current.close();
	}, []);

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text);

		Dialog.fire({
			showConfirmButton: false,
			text: `Successfully copied to clipboard!`,
			icon: "success",
			confirmButtonText: "Ok",
			timer: 1500,
		})
	};

	/**********************************************  ********************************************************/
	/********************************* HOOKS MUST BE ABOVE THIS LINE *****************************************/
	/**********************************************  ********************************************************/

	if (!attributes || !product) {
		actionBus?.send("load");
		return (
			<>
				<dialog id="no-product-found-dialog" ref={no_product_modal} onClick={close_no_product_dialog}>
					<div className="banner">Product Not Found</div>
				</dialog>

				{empty_loading_container}
			</>
		);
	}

	trackEvent("Item", "view", product.common_sku);

	// const startChat = () => {
	// 	setChatTarget(new Item(product));
	// 	const message = new ChatMessage({
	// 		target_class: "Item",
	// 		target_id: modalProps.item,
	// 	});
	//
	// 	addNewChatMessage(message);
	// 	setToggleSidebar(true);
	// 	setSidebarContent("chat");
	// };

	const access = catalog.options?.proposals?.access && currentUser;
	const addButtonAccess = access && (access !== "view")
		&& (product?.sell_price || product["data:sell_price"]);

	// downloadEnabled is initially true
	// but by deleting modalProps, modalProps is updated so it's rerendered
	// and now downloadEnabled is false
	const downloadEnabled = modalProps.enableDownload;
	delete modalProps.enableDownload;
	const variations = product?.variations || {};

	let qr_url;
	if (product.pdf) {
		const organization_member_data = JSON.parse(localStorage.getItem('organization_member:data')) || organizationMember;
		qr_url = ProductUtil.Make_Pdf_Qr_Code_Url({
			product,
			profile_token: organization_member_data?.profile?.public_token,
			organization_id: organization_member_data?.organization?.id,
		});
	}

	const on_qr_icon_click = e => {
		const target = e.target.parentNode;
		// add padding to media so image shift downward (cuz it's centered)
		const modal_content = target.closest(".modal-content");
		const footer_height = modal_content.querySelector(".modal-footer").getBoundingClientRect().height;
		modal_content.style.setProperty("--open-qr-modal-media-balance-padding", footer_height);
		setShowQr(true);
	}

	const on_modal_escape = e => {
		// if qr code open, only close that; not the whole detail
		if (showQr) {
			e.preventDefault();
			setShowQr(false);
		}
	}

	if (!product.media?.length && product.image_0) {
		product.media = [{
			source: product.image_0,
		}];
	}

	const catalogProductOptions = catalog.options?.product_attribute;
	const catalogAttributeInvariationOptions = catalogProductOptions?.invariation;
	// allow page-level overrides of catalog-level invariation configs
	const pageInvariationOptions = currentPage?.options?.product_attribute?.invariation ?? {};

	// merge the options together, allowing page-level options to override catalog-level ones
	const productAttributeInvariationOptions = ObjectUtil.DeepMerge({}, catalogAttributeInvariationOptions, pageInvariationOptions);
	// all options need to account for not being set. so for some undefined is not the same as false; double equal  null to account for null or undefined
	const allowInvariantVariationChange = productAttributeInvariationOptions?.allow_change == null || productAttributeInvariationOptions?.allow_change;
	const allowInvariantVariationContinueChange = productAttributeInvariationOptions?.allow_continue_change;
	const allowInvariantVariationMessage = productAttributeInvariationOptions?.warning?.message;
	const invariationOptions = {
		allow_change: allowInvariantVariationChange,
		allow_continue_change: allowInvariantVariationContinueChange,
		warning: {
			message: allowInvariantVariationMessage,
		},
	};
	const loadRelatedItems = items => {
		setRelatedItemCount(Object.values(items).reduce((agg, cur) => agg += cur.length, 0));
	};
	const invariationBus = new ActionBus()
		.on(ProductInvariationDialog.Actions.ACCEPT_INVARIATION_CHANGE, variation => changeItem(variation));

	const attributesBus = new ActionBus()
		.on(ProductInvariationDialog.Actions.INVARIATION_CHANGE, variation => {
			invariationBus.send(ProductInvariationDialog.Actions.SHOW, variation);
		});

	const VariationUnavailableModal = () => variations && Object.keys(variations).length && (
		<ProductInvariationDialog
			allowChange={allowInvariantVariationChange}
			allowContinue={allowInvariantVariationContinueChange}
			warningMessage={allowInvariantVariationMessage}
			actionBus={invariationBus}
		/>
	);
	const searchParams = new URLSearchParams((new URL(window.location)).search);
	const layout = searchParams.get('layout') || currentPage.options?.layout?.details;
	const container = (
		<div
			className={[
				"product-detail",
				loading && "is-loading",
				product.stone?.certificate && "is-stone",
				relatedItemCount && "has-related-items"].filter(v => v).join(" ")}>
			<div className="actions">
				<ProductDetailMenu item={product} catalog={catalog} close={closeModal} />
			</div>
			<div className="main">
				<div className={"media count-" + product.media?.length}>
					{product.media && (<ProductMedia media={product.media} key={product.id} />)}
				</div>
				<div className="product-attributes-wrapper" data-layout={layout}>
					<div id="product-info">
						{
							<ProductAttributes
								product={product}
								variations={variations}
								attributes={attributes}
								changeItem={changeItem}
								actionBus={attributesBus
								}
								invariationOptions={invariationOptions}
							/>
						}
						{
							product?.stone?.certificate && (
								<ProductAttribute attribute={{ reference: "certificate", label: "Certificate" }}
									data={product.stone.certificate} showLabel={true}>
									{
										product.stone.certificate_url ? (
											<a id="certificate-link" href={product.stone.certificate_url} target="_blank"
												rel="noreferrer">
												{product.stone.certificate}
											</a>
										) : (
											<>{product.stone.certificate}</>
										)
									}
								</ProductAttribute>
							)
						}
					</div>
					<div id="product-actions">
						<div className="share">
							{product.pdf && (
								<>
									<button
										className="copy-link"
										aria-label="Copy Link to Clipboard"
										onClick={() => copyToClipboard(product.pdf)}
									/>
									<button aria-label="Scan QR Code" className="qr-code" onClick={on_qr_icon_click}>
									</button>
									<div className="qr-code-wrapper">
										{showQr && (
											<ScannableCodeFullscreen
												url={qr_url.toString()}
												width={250}
												onClose={() => setShowQr(false)}
											/>
										)}
									</div>
									<button
										className="new-window"
										aria-label="Open in New Window"
										onClick={() => window.open(product.pdf)}
									/>
								</>
							)
							}
						</div>

						{!!addButtonAccess && <ProposalItemActions mode="button" item={product}></ProposalItemActions>}

						{currentUser && (<BookmarkItemButton itemId={modalProps.item} />)}

						{customerAllowed && <RequestProductButton productId={product.id} />}

						{/*{*/}
						{/*	currentUser && catalog?.options?.enable_chat*/}
						{/*	&& (*/}
						{/*		<Button type="button" className="chat" onClick={startChat}>*/}
						{/*			Have a Question for {catalog.options.branding.name}?*/}
						{/*		</Button>*/}
						{/*	)*/}
						{/*}*/}

						{
							catalog.options.item_inquiry?.enabled && product
							&& (<ItemInquiryButton form={catalog.options.item_inquiry.form} item={product} />)
						}
					</div>
				</div>
			</div>
			<React.Suspense fallback={<FontAwesomeIcon icon={faRotate} spin />}>
				<ItemRelationships itemId={modalProps.item} changeItem={changeItem} onLoad={loadRelatedItems} />
			</React.Suspense>
		</div>
	);

	const download_click = e => {
		e.stopPropagation();
		downloadMedia();
		LogContext.log({
			url: window.location.href,
			page: currentPage,
			action: "download",
			item: modalProps.item
		}, {
			limit: {
				type: "short",
				key: ["item", modalProps.item, "download"],
			}
		});
	}

	const detail_container = modalProps.item && (
		<>
			<VariationUnavailableModal />
			{(
				<Modal
					{...modalProps}
					show={modalProps.item}
					className={'_boss _boss-offcanvas product-detail-modal'}
					size="lg"
					aria-labelledby="contained-modal-title-vcenter"
					centered
					backdrop={false}
					enforceFocus={false}
					onEscapeKeyDown={on_modal_escape}
					css={SidebarContext.isOpen}
					onHide={closeModal}
					onShow={showModal}
				>
					<Modal.Body className="show-grid">
						{loading ? empty_loading_container : container}
					</Modal.Body>
					{
						<Modal.Footer>
							<Button onClick={closeModal}>Close</Button>

							{!!addButtonAccess && <ProposalItemActions item={product}></ProposalItemActions>}

							{downloadEnabled
								&&
								(
									<OverlayTrigger
										overlay={<Tooltip className="_boss tooltip-info">Download media</Tooltip>}>
										<button
											aria-label="Download"
											className="download"
											onClick={download_click}
										/>
									</OverlayTrigger>
								)
							}
						</Modal.Footer>
					}
				</Modal>
			)}
		</>
	);

	return detail_container;
}
